/**
 * "Does this part fit my machine?" - answered on the product page itself.
 *
 * The shopper's machine is the one they picked in the header finder, kept in localStorage. The
 * part's fitment is already on the page: snippets/fitment-variants.liquid writes out every
 * machine each variant is listed for, so this needs no extra request and no extra data.
 *
 * Every variant, not just the one on screen. A part can carry its machines on one option and not
 * another - a left and a right, a 10mm and a 12mm - and reading only the option on screen left
 * the badge silent on a part the finder had listed as a fit. When the answer is on another
 * option, the badge says which one and offers to switch to it.
 *
 * What it will and will not say
 * -----------------------------
 * It can say a part fits, and it can name the years or the versions it is established for and
 * ask the shopper to check. It never says a part does NOT fit. The catalogue records what a
 * manufacturer published, and silence in it is a gap in the record, not a statement that the
 * part is incompatible - a shopper turned away from a part that would have fitted is a worse
 * mistake than one asked to check.
 *
 * With no machine chosen yet it asks instead: how many vehicles the part fits, a way to the full
 * list, and a button that opens the header finder right here (see openFinder).
 *
 * The matching mirrors machine-results.js, which answers the same question for a whole list.
 */
(function () {
  "use strict";

  var GARAGE_KEY = "ghs:garage";

  /**
   * The matching itself lives in fitment-match.js, shared with the product finder and the results
   * page, so the three cannot drift into answering the same question differently. This file keeps
   * what is its own: reading the machine out of the garage, and wording one badge.
   */
  var F = window.GHSFitment;

  /**
   * The words, in the shopper's language, from snippets/fit-badge.liquid. Liquid's t filter
   * escapes what it returns and this file escapes again on the way out, so they are decoded once
   * here - otherwise a French apostrophe reads as &#39;.
   */
  var STRINGS = null;
  function say(key, fallback, vars) {
    if (!STRINGS) {
      STRINGS = {};
      var el = document.querySelector("[data-fit-badge-strings]");
      if (el) {
        try {
          var raw = JSON.parse(el.textContent) || {};
          var decoder = document.createElement("textarea");
          Object.keys(raw).forEach(function (k) {
            decoder.innerHTML = String(raw[k] || "");
            STRINGS[k] = decoder.value;
          });
        } catch (e) {}
      }
    }
    var text = STRINGS[key];
    // A key missing from this language comes back from Liquid as a "translation missing" note.
    if (!text || /translation missing/i.test(text)) text = fallback;
    return String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, function (whole, name) {
      return vars && name in vars ? String(vars[name]) : whole;
    });
  }

  /** The option on screen. The theme tells us when it changes; until then, the page's own. */
  var selectedId = null;

  /** "2015-2019, 2021" as it is written on the page, back into ranges. */
  function parseYears(label) {
    var text = String(label || "").trim();
    if (!text || text === "—" || text === "-") return [];
    var ranges = [];
    text.split(",").forEach(function (part) {
      var bits = part.trim().split("-");
      var start = parseInt(bits[0], 10);
      if (isNaN(start)) return;
      var end = bits.length > 1 ? parseInt(bits[1], 10) : start;
      ranges.push([start, isNaN(end) ? start : end]);
    });
    return ranges;
  }

  function readGarage() {
    try {
      var raw = window.localStorage.getItem(GARAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  /** Every variant that carries a fitment list, as snippets/fitment-variants.liquid wrote them. */
  function readVariants() {
    var el = document.querySelector("[data-fitment-variants]");
    if (!el) return [];
    try {
      var rows = JSON.parse(el.textContent);
      return Array.isArray(rows) ? rows : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Settles one variant's fitment against one machine and one year.
   *
   *   fits       their machine, and their year or no year in question
   *   version    their machine, but established only for named versions of it
   *   year       their machine, but the years published for it are not theirs
   *
   * And for a listing the build flags as a range - SPORTSMAN, with no engine - which says nothing
   * about which machine of the range it is for:
   *
   *   version    an accessory: "Fits the Polaris Sportsman range - check your model"
   *   unknown    a part: listed for the range, size not stated, ask the shop
   *
   *   null       nothing in this list speaks about their machine
   */
  function verdictFor(rows, machine) {
    var make = String(machine.make || "").toUpperCase().trim();
    var model = F.normalizeModel(machine.model);
    var year = parseInt(machine.year, 10) || null;

    var covering = []; // listings that cover their machine, exactly or as a family
    var versions = []; // other versions of the same machine
    var yearsSeen = [];
    var range = null;  // a listing for their machine's range, flagged by the build

    (rows || []).forEach(function (row) {
      if (String(row.make || "").toUpperCase().trim() !== make) return;
      var spelled = F.normalizeModel(row.model);
      var tier = F.classifyMatch(model, spelled);
      var ranges = parseYears(row.years);
      var entry = { model: spelled, ranges: ranges };
      /**
       * Read as covering theirs, a bare SPORTSMAN told a 570 owner a brake pad fitted. It names
       * the range, so the answer is about the range - see the end of this function.
       */
      if (row.range) {
        if (tier !== "similar" && (!range || row.range === "accessory")) range = { kind: row.range, model: spelled, ranges: ranges };
        return;
      }
      if (tier === "similar") {
        /**
         * Only a version of the same machine is worth mentioning; a different machine of the
         * same make says nothing at all.
         *
         * Read by classifying the other way round: their machine covers this listing, so the
         * listing is their machine plus a version word - MXZ 600 LE against their MXZ 600,
         * ALTERRA 550 TRV XT against their ALTERRA 550. A plain prefix test could not see it,
         * because the catalogue writes one machine both ways round - ALTERRA 550 TRV XT and 550
         * ALTERRA TRV XT - and the half it missed came out as "Not listed for your machine" on a
         * part the finder had just shown them as a fit.
         */
        if (F.classifyMatch(spelled, model) === "family") versions.push(entry);
        return;
      }
      covering.push(entry);
      ranges.forEach(function (r) { yearsSeen.push(r); });
    });

    if (covering.length) {
      // No years published at all: outdoor equipment is not sold by model year, so the machine
      // matching is the whole of the confirmation, and it is real confirmation.
      var anyYears = covering.some(function (c) { return c.ranges.length > 0; });
      // The machine is named on the line underneath, so the headline does not repeat it.
      if (!anyYears) return { state: "fits", text: say("fits_your_machine", "Fits your machine") };
      if (!year) return { state: "fits", text: say("fits_years", "Fits {{ years }}", { years: F.formatRanges(yearsSeen) }) };
      var covered = covering.some(function (c) {
        return c.ranges.some(function (r) { return year >= r[0] && year <= r[1]; });
      });
      if (covered) return { state: "fits", text: say("fits_your_machine", "Fits your machine") };
      // Their machine, other years. Say which, rather than claiming it will not work.
      return { state: "year", text: say("fits_years_check", "Fits {{ years }} - check your year", { years: F.formatRanges(yearsSeen) }) };
    }

    if (versions.length) {
      /**
       * It fits their machine - but only the versions of it named here. Said the way the results
       * page says it, so a shopper meets one sentence about the part on the card and the same
       * sentence on the product page. The version is what they check against their own machine,
       * so it is the headline rather than a footnote.
       *
       * Each version once: the catalogue files the same version under several year ranges, and a
       * badge reading "Fits the LE, LE, LE version" is a badge nobody trusts.
       */
      var seen = {};
      var named = [];
      versions.forEach(function (v) {
        var name = F.readableTrim(F.trimAgainst(model, v.model));
        if (!name || seen[name]) return;
        seen[name] = true;
        named.push(name);
      });
      if (named.length) {
        var text = named.length === 1
          ? say("fits_version", "Fits the {{ versions }} version only - check yours", { versions: F.trimList(named) })
          : say("fits_versions", "Fits the {{ versions }} versions only - check yours", { versions: F.trimList(named) });
        return { state: "version", text: text };
      }
    }

    if (range) {
      var label = titleCase(make + " " + range.model.replace(/\s+ALL$/, ""));
      if (range.kind !== "accessory") {
        return { state: "unknown", text: say("range_part", "Listed for the {{ range }} range - size not stated, ask us", { range: label }) };
      }
      var inYear = !year || !range.ranges.length || range.ranges.some(function (r) { return year >= r[0] && year <= r[1]; });
      if (!inYear) return { state: "year", text: say("fits_years_check", "Fits {{ years }} - check your year", { years: F.formatRanges(range.ranges) }) };
      return { state: "version", text: say("fits_range", "Fits the {{ range }} range - check your model", { range: label }) };
    }

    return null;
  }

  /**
   * The best answer any option other than the one on screen can give. "Best" because a part sold
   * as a left and a right is answered by whichever side fits, and that is the one worth naming.
   */
  var RANK = { fits: 3, version: 2, year: 1, unknown: 0 };
  function answerElsewhere(variants, machine) {
    var best = null;
    variants.forEach(function (variant) {
      var verdict = verdictFor(variant.machines, machine);
      if (!verdict) return;
      if (!best || RANK[verdict.state] > RANK[best.verdict.state]) {
        best = { variant: variant, verdict: verdict, count: 1 };
      } else if (RANK[verdict.state] === RANK[best.verdict.state]) {
        best.count += 1;
      }
    });
    return best;
  }

  function paint() {
    var box = document.querySelector("[data-fit-badge-box]");
    if (!box) return;

    /**
     * No fitment list anywhere on this product - a helmet, a bottle of oil, a jacket. Nothing has
     * been said about machines here and nothing should be: a badge on a helmet reading "not
     * listed for your machine" is noise dressed up as information.
     */
    var variants = readVariants();
    if (!variants.length) {
      box.hidden = true;
      return;
    }

    var id = String(selectedId || box.getAttribute("data-fit-badge-variant") || "");
    var chosen = null;
    var others = [];
    variants.forEach(function (variant) {
      if (String(variant.id) === id) chosen = variant;
      else others.push(variant);
    });

    var machine = readGarage();
    if (!machine || !machine.make || !machine.model) {
      paintPrompt(box, chosen || variants[0]);
      return;
    }

    var state;
    var headline;
    var link = null;
    var verdict = chosen ? verdictFor(chosen.machines, machine) : null;

    if (verdict) {
      state = verdict.state;
      headline = verdict.text;
    } else {
      /**
       * The option on screen has nothing to say. Another one might: the fitment is filed per
       * variant, and a part filed under one option is not filed under the rest. Naming the option
       * is the whole point - "it fits, but not the one you are looking at" is only useful with
       * the one they should be looking at attached.
       */
      var elsewhere = answerElsewhere(others, machine);
      if (elsewhere && elsewhere.count === 1) {
        var title = elsewhere.variant.title;
        state = "option";
        headline = elsewhere.verdict.state === "fits"
          ? say("option_fits", 'Fits your machine - but only the "{{ option }}" option', { option: title })
          : say("option_may_fit", 'The "{{ option }}" option may fit - check that one', { option: title });
        if (elsewhere.variant.url) {
          link = { url: elsewhere.variant.url, text: say("switch_to", 'Switch to "{{ option }}"', { option: title }) };
        }
      } else if (elsewhere) {
        // More than one option answers, so naming one would send them to the wrong one.
        state = "option";
        headline = elsewhere.verdict.state === "fits"
          ? say("others_fit", "Fits your machine - but only on other options")
          : say("others_may_fit", "Other options may fit - check them");
      } else {
        /**
         * This part does carry a fitment list, and their machine is on none of them. Still not
         * "it does not fit": these lists are what a manufacturer published, and they are known to
         * be incomplete. So the badge says exactly that much and no more.
         */
        state = "unknown";
        headline = say("not_listed", "Not listed for your machine");
      }
    }

    var name = (machine.year ? machine.year + " " : "") + machine.make + " " + machine.model;
    box.className = "fit-badge fit-badge-" + state;
    box.innerHTML =
      '<span class="fit-badge-mark" aria-hidden="true"></span>' +
      '<span class="fit-badge-text"><b>' + escapeHtml(headline) + "</b>" +
      (link ? '<a class="fit-badge-switch" href="' + escapeHtml(link.url) + '">' + escapeHtml(link.text) + "</a>" : "") +
      '<span class="fit-badge-machine">' + escapeHtml(say("your_machine", "Your machine: {{ machine }}", { machine: name })) +
      (finder()
        ? ' <button type="button" class="fit-badge-change" data-fit-open-finder>' + escapeHtml(say("change_machine", "Change machine")) + "</button>"
        : "") +
      "</span></span>";
    box.hidden = false;
    wireButtons(box);
  }

  /**
   * No machine chosen yet. The part does carry a fitment list, so the question is asked rather
   * than left for the table two screens down: how many vehicles it fits, which makes and years
   * when that is short enough to read, the way to the full list, and the finder.
   */
  function paintPrompt(box, variant) {
    var rows = (variant && variant.machines) || [];
    if (!rows.length) {
      box.hidden = true;
      return;
    }

    var makes = [];
    var low = null;
    var high = null;
    rows.forEach(function (row) {
      var make = titleCase(row.make);
      if (make && makes.indexOf(make) === -1) makes.push(make);
      parseYears(row.years).forEach(function (r) {
        if (low === null || r[0] < low) low = r[0];
        if (high === null || r[1] > high) high = r[1];
      });
    });

    var summary;
    if (makes.length > 3) {
      summary = say("prompt_brands", "Fits {{ count }} vehicles from {{ brands }} brands.", {
        count: rows.length.toLocaleString(),
        brands: makes.length
      });
    } else {
      var list = makes.join(", ");
      if (low !== null) {
        list += ", " + (low === high ? String(low) : say("years_span", "{{ from }} to {{ to }}", { from: low, to: high }));
      }
      summary = rows.length === 1
        ? say("prompt_one", "Fits 1 vehicle: {{ list }}.", { list: list })
        : say("prompt_many", "Fits {{ count }} vehicles: {{ list }}.", { count: rows.length.toLocaleString(), list: list });
    }

    var table = document.querySelector(".product-fitment-section");
    box.className = "fit-badge fit-badge-prompt";
    box.innerHTML =
      '<span class="fit-badge-mark" aria-hidden="true"></span>' +
      '<span class="fit-badge-text"><b>' + escapeHtml(say("prompt_title", "Will it fit your machine?")) + "</b>" +
      '<span class="fit-badge-summary">' + escapeHtml(summary) +
      (table ? ' <a class="fit-badge-list" href="#' + escapeHtml(table.id) + '">' + escapeHtml(say("see_list", "See the list")) + "</a>" : "") +
      "</span>" +
      (finder()
        ? '<button type="button" class="fit-badge-check" data-fit-open-finder>' + escapeHtml(say("check_machine", "Check my machine")) + "</button>"
        : "") +
      "</span>";
    box.hidden = false;
    wireButtons(box);
  }

  /** "ARCTIC CAT" as the catalogue spells it, "Arctic Cat" as a sentence wants it. */
  function titleCase(value) {
    return String(value || "").trim().toLowerCase().replace(/(^|[\s\-\/])(\w)/g, function (whole, before, letter) {
      return before + letter.toUpperCase();
    });
  }

  /** The finder in the header, where the shopper picks their machine. */
  function finder() {
    return document.querySelector("[data-sbm-header]");
  }

  /**
   * Opens the header finder for this question. The finder is told to stay on this page when the
   * machine is chosen (data-sbm-stay), because the answer is here - on any other page its button
   * takes the shopper to the parts that fit. It then announces the new machine and this box
   * repaints.
   */
  function openFinder() {
    var root = finder();
    if (!root) return;
    root.setAttribute("data-sbm-stay", "");
    var toggle = root.querySelector("[data-sbm-toggle]");
    if (toggle && toggle.getAttribute("aria-expanded") !== "true") toggle.click();
    // Set again: opening can run after a close that clears it.
    root.setAttribute("data-sbm-stay", "");
    var top = root.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight * 0.6) root.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(function () {
      var first = root.querySelector("[data-sbm-body] select:not([disabled]), [data-sbm-body] input:not([type=hidden]):not([disabled])");
      if (first) first.focus({ preventScroll: true });
    }, 50);
  }

  function wireButtons(box) {
    Array.prototype.forEach.call(box.querySelectorAll("[data-fit-open-finder]"), function (button) {
      button.addEventListener("click", openFinder);
    });
    var list = box.querySelector(".fit-badge-list");
    if (list) {
      list.addEventListener("click", function (event) {
        var target = document.getElementById(list.getAttribute("href").slice(1));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", paint);
  else paint();

  /**
   * The fitment is per variant, so changing the option changes the answer. The theme announces
   * the change as product:variant-change; the hidden id field on the add-to-cart form changes
   * with it, and catches anything that sets the variant without announcing it. The old listener
   * here was for an event name nothing in the theme has ever fired.
   */
  document.addEventListener("product:variant-change", function (event) {
    if (event.detail && event.detail.variant) selectedId = String(event.detail.variant.id);
    paint();
  });
  /**
   * The same answer for the cart page (assets/cart-fit.js), so a part reads "fits" or "check"
   * in the cart exactly as it did on its own page.
   */
  window.GHSFitAnswer = {
    readGarage: readGarage,
    verdictFor: verdictFor,
    say: say
  };

  // A machine picked or cleared in the header finder, on this page.
  document.addEventListener("ghs:garage-change", paint);
  document.addEventListener("change", function (event) {
    var input = event.target;
    if (!input || input.name !== "id" || !input.value) return;
    selectedId = String(input.value);
    paint();
  });

  // A combined listing swaps the whole section - a different product, a fresh fitment list.
  if (typeof subscribe === "function") {
    subscribe("section-refreshed", function () {
      selectedId = null;
      window.setTimeout(paint, 0);
    });
  }
})();
